import {selectCurrentPlayer} from '../../redux/reducers/sharedStateReducer/selectors';
import {
  checkIfMomentReached,
  createMissionMoment,
  MISSION_SCRIPT_KINDS,
} from '../../configs/campaigns/missionFormat';
import {missionAdvanceScript} from '../../redux/actions';

/**
 * Проигрывание записанных ходов противников в сюжетной миссии.
 *
 * Устроено так же, как скриптованный бот обучения (tutorialBotScript): хук
 * вызывается из executeBotStep ДО обычного decision-движка и отвечает, что
 * делать этим тиком. Разница в том, что обучение исполняет высокоуровневые
 * операции из конфига главы, а миссия — сырые redux-экшены, записанные
 * редактором.
 *
 * Возвращает:
 *   null                 — миссия не идёт, работаем как обычно;
 *   MISSION_BOT_WAITING  — ждём игрока (открыта реплика) — тик не считается шагом;
 *   true                 — записанное действие воспроизведено;
 *   false                — записи для этого игрока кончились (или сценарий
 *                          разошёлся с партией) — пусть ходит обычный ИИ.
 */
export const MISSION_BOT_WAITING = 'MISSION_BOT_WAITING';

// Момент записи уже позади: раунд партии ушёл дальше записанного
export const checkIfMomentPassed = (moment, currentMoment) => Boolean(moment)
  && currentMoment.round > moment.round;

export const maybeExecuteMissionBotStep = (store) => {
  const state = store.getState();
  const mission = state.private?.mission;

  // Редактор сам ничего не воспроизводит: там автор играет за всех вручную,
  // а переключённых на ИИ ведёт обычный движок ботов
  if (!mission || mission.isEditorMode || !mission.missionId) {
    return null;
  }
  if (mission.isMissionCompleted) {
    return false;
  }
  // Пока на экране реплика, партия стоит: игрок читает текст
  if (mission.activeReplica) {
    return MISSION_BOT_WAITING;
  }

  const entry = mission.script[mission.scriptIndex];
  if (!entry) {
    // Сценарий кончился — дальше противник играет сам
    return false;
  }
  // Реплики, задания и переключения на ИИ обрабатывает MissionRunner:
  // они могут выпадать и на ход живого игрока, когда тиков бота нет вовсе
  if (entry.kind !== MISSION_SCRIPT_KINDS.ACTION) {
    return MISSION_BOT_WAITING;
  }

  const currentPlayer = selectCurrentPlayer(state);
  if (!currentPlayer || entry.playerColor !== currentPlayer.color) {
    return false;
  }

  const currentMoment = createMissionMoment(state);
  if (!checkIfMomentReached(entry.moment, currentMoment)) {
    // Партия разошлась с записью (игрок сыграл иначе) — не ломаем ход,
    // отдаём его обычному ИИ; запись останется ждать своего момента
    return false;
  }

  // Запись сделана на поле редактора, а живой игрок волен занять ту же
  // клетку. Редьюсер MOVE_UNIT занятость не проверяет (в живой партии это
  // делает интерфейс), поэтому такой ход поставил бы два отряда в один гекс —
  // пропускаем его, остальную ленту играем дальше
  if (checkIfRecordedMoveBlocked(entry.action, state)) {
    console.warn('[Mission] записанный ход отменён: гекс занят —', getRecordedMoveTarget(entry.action));
    store.dispatch(missionAdvanceScript(mission.scriptIndex));
    return true;
  }

  store.dispatch({...entry.action, __missionScript: true});
  store.dispatch(missionAdvanceScript(mission.scriptIndex));

  return true;
};

// Перемещение записанного отряда на клетку, где уже кто-то стоит.
// Проверять надо ОБА экшена перемещения: MOVE_UNIT переносит отряд, но и
// ADD_PENDING_MOVEMENT_ACTION в своём редьюсере тоже переставляет hexKey —
// пропустив только первый, мы бы получили тот же стек отрядов вторым
const MOVE_ACTION_TARGETS = {
  MOVE_UNIT: 'newHexKey',
  ADD_PENDING_MOVEMENT_ACTION: 'toHexKey',
};

const getRecordedMoveTarget = (action) => {
  const targetField = MOVE_ACTION_TARGETS[action.type];
  return targetField ? action.payload?.[targetField] : null;
};

const checkIfRecordedMoveBlocked = (action, state) => {
  const targetHexKey = getRecordedMoveTarget(action);
  if (!targetHexKey) return false;

  return (state.shared.gameState.units || []).some((unit) => (
    unit.hexKey === targetHexKey && unit.id !== action.payload.unitId
  ));
};
